/**
 * Sub-aba Ocupação & FTE — lista de setores (esquerda) + painel detalhado (direita).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Activity,
  AlertTriangle,
  Target,
  Users,
} from 'lucide-react';
import type { DeptUnit } from '../../../services/executiveDeptUnitsService';
import { DeptCapacitySidePanel } from './DeptCapacitySidePanel';
import { OpsOperatorsAtRiskBlock } from '../executive/OpsOperatorsAtRiskBlock';
import { useTeamCapacityData } from './useTeamCapacityData';
import { useDeptUnitAggregates } from './useDeptUnitAggregates';

function FteBadge({ load }: { load: number }) {
  if (load <= 0) return <span className="text-[9px] text-[var(--text-muted)]">0 FTE</span>;
  const level = load > 8 ? 'overload' : load > 5 ? 'high' : 'normal';
  const dot = { normal: 'bg-emerald-500', high: 'bg-amber-500', overload: 'bg-red-500' }[level];
  const text = { normal: 'text-emerald-600', high: 'text-amber-600', overload: 'text-red-600' }[level];
  const label = Number.isInteger(load) ? String(load) : load.toFixed(1);
  return (
    <span className={`inline-flex items-center gap-1 text-[9px] font-semibold tabular-nums ${text}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />
      {label}
    </span>
  );
}

export interface OpsBenchmarkOccupationPanelProps {
  organizationId: string;
  units: DeptUnit[];
  isLoading?: boolean;
  isFallback?: boolean;
  departmentId?: string;
  periodLabel: string;
  highlightUserId?: string | null;
}

export const OpsBenchmarkOccupationPanel: React.FC<OpsBenchmarkOccupationPanelProps> = ({
  organizationId,
  units,
  isLoading,
  isFallback,
  departmentId,
  periodLabel,
  highlightUserId = null,
}) => {
  const [selectedDeptId, setSelectedDeptId] = useState<string | null>(null);

  const visibleUnits = useMemo(() => {
    if (!departmentId) return units;
    return units.filter((u) => u.department.id === departmentId);
  }, [units, departmentId]);

  const { aggregatesByDeptId } = useDeptUnitAggregates(organizationId, visibleUnits);

  const scopeMemberIds = useMemo(() => {
    if (departmentId) {
      const scoped = aggregatesByDeptId.get(departmentId)?.memberUserIds;
      if (scoped?.length) return scoped;
      const unit = units.find((u) => u.department.id === departmentId);
      if (unit?.members.length) return unit.members.map((m) => m.user_id);
    }
    const all = new Set<string>();
    for (const agg of aggregatesByDeptId.values()) {
      for (const id of agg.memberUserIds) all.add(id);
    }
    if (all.size > 0) return [...all];
    return units.flatMap((u) => u.members.map((m) => m.user_id));
  }, [aggregatesByDeptId, units, departmentId]);

  const teamSnapshot = useTeamCapacityData(organizationId, {
    memberUserIds: scopeMemberIds.length > 0 ? scopeMemberIds : undefined,
    departmentId,
    refetchInterval: 30_000,
  });

  useEffect(() => {
    if (visibleUnits.length === 0) {
      setSelectedDeptId(null);
      return;
    }
    const stillVisible = visibleUnits.some((u) => u.department.id === selectedDeptId);
    if (!selectedDeptId || !stillVisible) {
      setSelectedDeptId(visibleUnits[0].department.id);
    }
  }, [visibleUnits, selectedDeptId]);

  useEffect(() => {
    if (!highlightUserId) return;
    const unit = visibleUnits.find((item) => item.members.some((member) => member.user_id === highlightUserId));
    if (unit) setSelectedDeptId(unit.department.id);
  }, [highlightUserId, visibleUnits]);

  const selectDept = useCallback((deptId: string) => {
    setSelectedDeptId(deptId);
  }, []);

  const selectedUnit = useMemo((): DeptUnit | null => {
    if (!selectedDeptId) return null;
    const unit = visibleUnits.find((u) => u.department.id === selectedDeptId);
    if (!unit) return null;
    const agg = aggregatesByDeptId.get(selectedDeptId);
    if (!agg) return unit;
    return {
      ...unit,
      members: agg.members,
      manager: agg.manager ?? unit.manager,
      projects: unit.projects,
      actionCount: agg.actionCount,
      overdueCount: agg.overdueCount,
      fteCount: agg.teamCount,
    };
  }, [selectedDeptId, visibleUnits, aggregatesByDeptId]);

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col gap-2 overflow-hidden">
      <p className="shrink-0 text-[10px] text-[var(--text-muted)]">{periodLabel}</p>
      <OpsOperatorsAtRiskBlock members={teamSnapshot.members} />

      <div className="flex min-h-0 flex-1 gap-2 overflow-hidden">
        {/* Lista de departamentos — coluna esquerda */}
        <div className="flex w-[min(320px,34%)] min-w-[220px] shrink-0 flex-col overflow-hidden rounded-sm border border-[var(--border-default)]/70 bg-[var(--bg-surface)]">
          <div className="flex shrink-0 items-center justify-between border-b border-[var(--border-default)]/60 bg-[var(--bg-elevated)]/80 px-3 py-2">
            <div className="flex items-center gap-1.5">
              <Users size={12} className="text-[var(--text-secondary)]" />
              <span className="text-[9px] font-bold uppercase tracking-wider text-slate-800 dark:text-slate-100">
                Departamentos
              </span>
              {isFallback && (
                <span className="rounded-sm border border-amber-400/20 bg-amber-500/10 px-1 py-px text-[8px] font-medium text-amber-600">
                  Padrão
                </span>
              )}
            </div>
            <span className="text-[9px] text-[var(--text-muted)]">
              {isLoading ? '…' : visibleUnits.length}
            </span>
          </div>

          {isFallback && !isLoading && (
            <div className="flex shrink-0 items-center gap-1.5 border-b border-amber-400/20 bg-amber-500/5 px-3 py-1.5 text-[9px] text-amber-700 dark:text-amber-400">
              <AlertTriangle size={10} className="shrink-0" />
              Configure em Organização → Estrutura
            </div>
          )}

          <div className="min-h-0 flex-1 overflow-y-auto">
            {isLoading ? (
              <div className="py-6 text-center text-[10px] text-[var(--text-muted)] animate-pulse">
                Carregando setores…
              </div>
            ) : visibleUnits.length === 0 ? (
              <div className="py-6 text-center text-[10px] text-[var(--text-muted)]">
                Nenhum departamento encontrado
              </div>
            ) : (
              <ul className="divide-y divide-[var(--border-default)]/40">
                {visibleUnits.map((unit) => {
                  const agg = aggregatesByDeptId.get(unit.department.id);
                  const teamCount = agg?.teamCount ?? 0;
                  const fteLoad = agg?.fteLoad ?? 0;
                  const actionCount = agg?.actionCount ?? 0;
                  const overdueCount = agg?.overdueCount ?? 0;
                  const projectCount = agg?.projectCount ?? 0;
                  const isSelected = selectedDeptId === unit.department.id;
                  const deptColor = unit.department.color ?? '#64748b';

                  return (
                    <li key={unit.department.id}>
                      <button
                        type="button"
                        onClick={() => selectDept(unit.department.id)}
                        className={`flex w-full flex-col gap-1 px-3 py-2 text-left transition-colors ${
                          isSelected
                            ? 'border-l-[3px] bg-blue-500/[0.06]'
                            : 'border-l-[3px] border-l-transparent hover:bg-[var(--button-hover)]/40'
                        }`}
                        style={isSelected ? { borderLeftColor: deptColor } : undefined}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex min-w-0 items-center gap-1.5">
                            <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: deptColor }} />
                            <span className={`truncate text-[12px] font-semibold ${isSelected ? 'text-[var(--text-primary)]' : 'text-[var(--text-secondary)]'}`}>
                              {unit.department.name}
                            </span>
                          </div>
                          <FteBadge load={fteLoad} />
                        </div>
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 pl-3.5 text-[9px] text-[var(--text-muted)]">
                          <span className="inline-flex items-center gap-0.5">
                            <Users size={9} />
                            {teamCount}
                          </span>
                          <span className="inline-flex items-center gap-0.5">
                            <Target size={9} />
                            {projectCount}
                          </span>
                          {actionCount > 0 ? (
                            <span className={`inline-flex items-center gap-0.5 font-medium ${overdueCount > 0 ? 'text-red-500' : 'text-amber-600'}`}>
                              <Activity size={9} />
                              {actionCount}
                              {overdueCount > 0 && ` (${overdueCount}a)`}
                            </span>
                          ) : null}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        {/* Painel detalhado — coluna direita */}
        <div className="flex min-w-0 flex-1 flex-col overflow-hidden rounded-sm border border-[var(--border-default)]/70 bg-[var(--bg-surface)]">
          {selectedUnit ? (
            <DeptCapacitySidePanel
              key={`${selectedUnit.department.id}:${highlightUserId ?? ''}`}
              organizationId={organizationId}
              unit={selectedUnit}
              periodLabel={periodLabel}
              initialUserId={highlightUserId}
            />
          ) : (
            <div className="flex h-full flex-col items-center justify-center gap-2 px-6 text-center">
              <Users size={28} className="text-[var(--text-muted)]/40" />
              <p className="text-[12px] font-medium text-[var(--text-secondary)]">
                Selecione um departamento para comparar KPIs, operadores e gargalos
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OpsBenchmarkOccupationPanel;
